import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Clock, Trophy, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useClickOutside } from '@/hooks/useClickOutside';
import type { SnakeCurrencyDto, SnakeResolutionPeriodDto, SnakeSeriesResponseDto } from '@/lib/models';
import { formatSpot, formatWindowEt, formatClock, parsePrice } from '@/lib/snake-market';

export interface SnakeMarketHeaderProps {
  /** The active currency — supplies the asset icon + display name. */
  currency: SnakeCurrencyDto;
  /** The current series instance. */
  series: SnakeSeriesResponseDto;
  /** Resolution periods for the active currency, ordered closest-first. */
  periods: SnakeResolutionPeriodDto[];
  /** The active resolution sub-category code. */
  activeSubCategoryCode: string;
  /** Fired with the selected sub-category code when the dropdown changes. */
  onResolutionChange: (subCategoryCode: string) => void;
  /** Live underlying spot (dollars), or `null` until the first tick. */
  spot: number | null;
  /** Clock in unix-ms driving the countdown. */
  nowMs: number;
  /** True once this window has closed — the timer becomes a "Go To Live" action. */
  closed?: boolean;
  /** Tapped from the "Go To Live" pill to jump to the current live instance. */
  onGoLive?: () => void;
  /** True while the price-to-beat strike is still resolving (boundary race). */
  priceToBeatPending?: boolean;
  /**
   * When set, the header renders a **settled past window** instead of the live
   * series: the strike/window come from this object, "Current Price" becomes
   * "Final Price" (the settled price, coloured by `wonSide`), and the countdown
   * is replaced by the "Go To Live" action. `null` = live view.
   */
  pastView?: {
    strike: number | null;
    finalPrice: number | null;
    wonSide: string | null;
    openTime: string | null;
    closeTime: string | null;
  } | null;
}

/**
 * Snake market card header (ARC-84 §5, Figma node 1659-69464).
 *
 * Asset icon + `{asset} Up or Down` title, the ET market window subtitle, a
 * top-right resolution dropdown (replaces the standalone resolution chips), and
 * an info row with the **Price to Beat** (`outcome.strikePrice`) / **Current
 * Price** (live `spot`, green at/above strike, red below) pill plus a separate
 * `HH:MM:SS` timer pill counting down to the series close.
 */
export function SnakeMarketHeader({
  currency,
  series,
  periods,
  activeSubCategoryCode,
  onResolutionChange,
  spot,
  nowMs,
  closed,
  onGoLive,
  priceToBeatPending,
  pastView,
}: SnakeMarketHeaderProps) {
  const viewingPast = Boolean(pastView);
  const strike = pastView ? pastView.strike : parsePrice(series.outcome?.strikePrice ?? null);
  // Right pill: the settled Final Price in past view, else the live spot
  // (falling back to the strike before the first tick).
  const priceValue = pastView ? pastView.finalPrice : (spot ?? strike);
  const priceLabel = viewingPast ? 'Final Price' : 'Current Price';
  // Colour: by settled result in past view (fall back to value-vs-strike when
  // the result isn't set), else live value-vs-strike.
  let isUp: boolean;
  if (pastView && pastView.wonSide === 'UP') isUp = true;
  else if (pastView && pastView.wonSide === 'DOWN') isUp = false;
  else isUp = priceValue != null && strike != null ? priceValue >= strike : true;
  const priceTextClass = isUp ? 'text-up' : 'text-down';
  // The price-to-beat is always known for a settled window; the pending state is
  // a live boundary race only.
  const beatPending = !viewingPast && priceToBeatPending;
  // Past windows are closed by definition → always offer "Go To Live".
  const showGoLive = closed || viewingPast;

  const title = `${currency.shortName ?? currency.name ?? currency.instrument} Up or Down`;
  const window = pastView
    ? formatWindowEt(pastView.openTime, pastView.closeTime)
    : formatWindowEt(series.openTime, series.closeTime);

  return (
    // The header sizes itself by its own width (@container), NOT the viewport:
    // the desktop main column ranges ~480px (1024 with the 380px trade rail) to
    // ~950px (1920), so viewport breakpoints can't tell "roomy card" from
    // "cramped card" — the 1920-Figma sizes used to overflow at 1024–1440
    // (labels collapsed, prices painting over the divider/badge).
    <div className="@container rounded-[12px] bg-surface">
      {/* Title row: icon + title + window, with the resolution dropdown top-right.
          Scales up per Figma node 6678:169750 (64px icon, 28/20px text) only when
          the card itself is wide enough (@3xl = 768px card width); the lg padding
          drops because the parent chart card supplies it. */}
      <div className="flex items-start justify-between gap-2 px-3.5 pt-3.5 lg:p-0 @3xl:items-center">
        <div className="flex min-w-0 items-start gap-3 @3xl:items-center @3xl:gap-2">
          {currency.imageUrl ? (
            <img
              src={currency.imageUrl}
              alt=""
              className="size-[50px] shrink-0 rounded-full object-cover @3xl:size-[64px]"
            />
          ) : (
            <div className="size-[50px] shrink-0 rounded-full bg-stroke-soft @3xl:size-[64px]" />
          )}
          <div className="flex min-w-0 flex-col">
            <h1 className="m-0 truncate text-[20px] font-medium leading-snug text-ink @max-[20rem]:text-[18px] @2xl:text-[24px] @3xl:text-[28px]">
              {title}
            </h1>
            <span className="truncate text-[16px] tracking-[-0.32px] text-ink @max-[20rem]:text-[14px] @3xl:text-[20px] @3xl:tracking-[-0.4px]">
              {window}
            </span>
          </div>
        </div>
        <ResolutionDropdown
          periods={periods}
          activeSubCategoryCode={activeSubCategoryCode}
          onResolutionChange={onResolutionChange}
        />
      </div>

      {/* Info row (Figma node 1657:64421): Price to Beat / Current Price pill on
          the left, the countdown — or "Go To Live" once closed (node 1855:59662)
          — on the right. ALWAYS one row. Compact stacked stats (label above
          value) by default; the wide inline variant (labels beside values,
          stats spread around a centred divider, node 6678:169616) switches on
          only at @3xl, where it is guaranteed to fit. Labels truncate first if
          space still runs out, then the prices ellipsize as an absolute last
          resort — nothing ever paints over a neighbour. */}
      <div className="flex items-center justify-between gap-1.5 px-3.5 pb-3.5 pt-3 sm:gap-2 lg:p-0 @3xl:gap-6">
        <div className="flex min-w-0 items-center gap-1.5 rounded-[44px] bg-surface-soft px-1.5 py-1.5 sm:gap-2 sm:px-2.5 @3xl:h-[53px] @3xl:flex-1 @3xl:justify-between @3xl:gap-3 @3xl:px-8">
          {/* Price to Beat */}
          <div className="flex min-w-0 items-center gap-1.5 @3xl:gap-3">
            {/* The stat icons are decorative — on very narrow cards (@max-sm)
                they'd steal the width the prices need, so they drop out first. */}
            <span className="hidden size-5 shrink-0 items-center justify-center rounded-full bg-gold/20 @sm:flex @3xl:size-6">
              <Trophy className="size-3 text-gold @3xl:size-3.5" />
            </span>
            <div className="flex min-w-0 flex-col leading-tight @3xl:flex-row @3xl:items-center @3xl:gap-2">
              <span className="truncate text-[13px] text-ink @max-[20rem]:text-[11px] @3xl:text-[18px] @3xl:text-ink-muted">
                Price to Beat
              </span>
              <span className="truncate text-[15px] font-medium tabular-nums text-ink @max-[20rem]:text-[13px] @3xl:text-[18px] @3xl:font-semibold">
                {beatPending ? '—' : formatSpot(strike)}
              </span>
            </div>
          </div>

          <span className="h-8 w-px shrink-0 bg-stroke-soft @3xl:h-6" />

          {/* Current Price */}
          <div className="flex min-w-0 items-center gap-1.5 @3xl:gap-3">
            <span
              className={`hidden size-5 shrink-0 items-center justify-center rounded-full @sm:flex @3xl:size-6 ${isUp ? 'bg-up/20' : 'bg-down/20'}`}
            >
              {isUp ? (
                <ArrowUp className={`size-3 @3xl:size-3.5 ${priceTextClass}`} />
              ) : (
                <ArrowDown className={`size-3 @3xl:size-3.5 ${priceTextClass}`} />
              )}
            </span>
            <div className="flex min-w-0 flex-col leading-tight @3xl:flex-row @3xl:items-center @3xl:gap-2">
              <span className="truncate text-[13px] text-ink @max-[20rem]:text-[11px] @3xl:text-[18px] @3xl:text-ink-muted">
                {priceLabel}
              </span>
              <span
                className={`truncate text-[15px] font-medium tabular-nums @max-[20rem]:text-[13px] @3xl:text-[18px] @3xl:font-semibold ${priceTextClass}`}
              >
                {formatSpot(priceValue)}
              </span>
            </div>
          </div>
        </div>

        {showGoLive ? (
          <button
            type="button"
            onClick={onGoLive}
            className="flex h-[38px] shrink-0 items-center justify-center gap-0.5 rounded-[44px] border-[1.5px] border-purple/30 px-2.5 text-[14px] font-semibold capitalize tracking-[-0.32px] text-purple"
          >
            Go To Live
            <ChevronRight className="size-3.5" />
          </button>
        ) : (
          <div className="flex h-[38px] shrink-0 items-center justify-center gap-1 rounded-[44px] bg-warning-soft px-2 sm:px-2.5 @3xl:px-3.5">
            <Clock className="size-3.5 text-warning" />
            <span className="text-[14px] font-semibold tracking-[-0.32px] text-warning tabular-nums @max-[20rem]:text-[12px] @3xl:text-[16px]">
              {formatClock(series.closeTime, nowMs)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Resolution period selector — purple underlined label + chevron, opens a menu. */
function ResolutionDropdown({
  periods,
  activeSubCategoryCode,
  onResolutionChange,
}: {
  periods: SnakeResolutionPeriodDto[];
  activeSubCategoryCode: string;
  onResolutionChange: (subCategoryCode: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const activeLabel = periods.find((p) => p.subCategoryCode === activeSubCategoryCode)?.label ?? periods[0]?.label ?? '';

  useClickOutside(rootRef, () => setOpen(false), open);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  function select(code: string) {
    setOpen(false);
    if (code !== activeSubCategoryCode) onResolutionChange(code);
  }

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-[18px] tracking-[-0.36px] text-purple"
      >
        <span className="underline">{activeLabel}</span>
        <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-full z-20 mt-1 flex min-w-[120px] flex-col gap-1 rounded-[16px] border-2 border-stroke-soft bg-surface-soft p-1.5 shadow-[0px_0px_4px_0px_var(--color-stroke-soft)]"
        >
          {periods.map((p) => {
            const active = p.subCategoryCode === activeSubCategoryCode;
            return (
              <li key={p.subCategoryCode}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => select(p.subCategoryCode)}
                  className={cn(
                    'w-full cursor-pointer rounded-[9px] border-none px-2.5 py-1.5 text-left text-[14px] text-ink',
                    active ? 'bg-stroke-soft' : 'bg-transparent hover:bg-surface',
                  )}
                >
                  {p.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
